import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Button as Button95, Hourglass, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Image } from 'expo-image';
import { Meta } from '@/components/Meta';
import { PasswordShowHideButton } from '@/components/PasswordShowHideButton';
import { PasswordRequirementsButton } from '@/components/PasswordRequirementsButton';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  userInput: {
    textAlign: 'center',
    backgroundColor: 'transparent',
    fontFamily: 'sans-serif',
    width: "100%",
  },
});

export default function Password() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const passwordInput = useRef<RNTextInput>(null);
  const router = useRouter();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const dispatch = useDispatch();
  const [newPasswordShown, setNewPasswordShown] = useState(false);

  const { register, control, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      password: '',
    }
  });

  const [password] = watch(["password"]);

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "Password.onSubmit",
        "/rabbl/api/set_password",
        "POST",
        {
          domain: domain,
          password: data.password,
        }
      );
      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));
      router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "Password.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [domain]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (passwordInput && passwordInput.current) {
        passwordInput.current?.focus();
      }
    }
  }, [passwordInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Password"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <AppText>Set a password</AppText>

          {
            // Use a text input to hopefully get the browser to save the email/password combo
          }
          <View style={AppConstants.STYLES.passwordInputRow}>
            <RNTextInput
              value={communityMembership.user_info.email}
              placeholder=""
              editable={false}
              returnKeyType="next"
              autoComplete="username"
              textContentType="username"
              style={styles.userInput}
            />
            {AppUtils.PASSWORD_REQUIREMENTS_BLANK}
          </View>

          <Controller
            control={control}
            name="password"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={passwordInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Password"
                  style={styles.formElement}
                  returnKeyType="next"
                  onBlur={onBlur}
                  autoComplete="password"
                  textContentType="password"
                  secureTextEntry={newPasswordShown ? false : true}
                  onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                />
                <PasswordRequirementsButton />
                <PasswordShowHideButton passwordShown={newPasswordShown} setPasswordShown={setNewPasswordShown} />
              </View>
            )}
          />
          {errors.password && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^^ Password is required.</AppText>}

          <View style={styles.inputWrapper}>
            <Button95
              onPress={handleSubmit(onSubmit, onValidationErrors)}
              disabled={password == ''}
            >Next</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
